import { useContext, useEffect } from 'react';
import { UNSAFE_DataRouterContext, useLocation, type Location } from 'react-router-dom';

/** How long a page gets to grow tall enough. After that, its position is lost. */
const GIVE_UP_MS = 5_000;

/** Someone moving the page themselves: their position wins from then on. */
const INPUT_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

/**
 * The key <ScrollRestoration /> saves a position under: the history entry, so
 * Back and Forward find it. Not the path: the router reads saved positions on
 * every navigation, so a key per path would restore on a fresh link too.
 *
 * Every page load starts on an entry keyed "default", and saved positions
 * outlive the page, so on that key alone, coming back from Stripe would land at
 * wherever the last loaded page was left. Those go by URL instead.
 */
export function scrollKey(location: Location): string {
  return location.key === 'default' ? location.pathname + location.search : location.key;
}

/**
 * <ScrollRestoration /> puts the page back once, as the navigation lands. A page
 * that draws its data a moment later (the account pages) isn't tall enough yet,
 * so the position clamps and stays lost. After a reload it doesn't get that far:
 * the router's update comes before RouterProvider subscribes to it, so the page
 * isn't moved at all. This finishes the job in both cases, as the page grows.
 */
export function useRestoreAsPageGrows() {
  const router = useContext(UNSAFE_DataRouterContext)?.router;
  const { key } = useLocation();

  useEffect(() => {
    const y = router?.state.restoreScrollPosition;
    if (typeof y !== 'number' || window.scrollY >= y - 1) return;

    const stop = () => {
      observer.disconnect();
      window.clearTimeout(timer);
      for (const name of INPUT_EVENTS) window.removeEventListener(name, stop);
    };
    const observer = new ResizeObserver(() => {
      window.scrollTo(0, y);
      if (window.scrollY >= y - 1) stop();
    });
    const timer = window.setTimeout(stop, GIVE_UP_MS);
    for (const name of INPUT_EVENTS) window.addEventListener(name, stop, { passive: true });
    observer.observe(document.body);
    return stop;
  }, [router, key]);
}
